import {
  Button,
  Form,
  InputNumber,
  Modal,
  Select,
  Spin,
  Switch,
  Row,
  Col,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import cancelX from "../../../../../../Assets/svg/cancelXbutton.svg";
import { useForm } from "antd/es/form/Form";
import { httpService } from "../../../../../../services/httpService.service";
import exclamationMark from "../../../../../../Assets/svg/exclamationMark.svg";
import doneIcon from "../../../../../../Assets/svg/done.svg";

const { Option } = Select;

const MoveSiteVisitModal = ({
  visible,
  siteVisit,
  onClose,
  onSubmit,
  setMessage,
}) => {
  const [loading, setLoading] = useState({
    projects: false,
    sampleSize: false,
    submit: false,
  });
  const [projects, setProjects] = useState([]);
  const [availableSamples, setAvailableSamples] = useState([]);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [filteredSamples, setFilteredSamples] = useState([]);
  const [form] = useForm();
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setAvailableSamples([]);
      setSelectedSamples([]);
      setFilteredSamples([]);

      const contractId = localStorage.getItem("currentContractId");
      const currentProjectId = localStorage.getItem("currentProjectID");
      setLoading((prev) => ({ ...prev, projects: true }));

      httpService.get(
        `/api/Projects/getAll?contractId=${contractId}&pageNumber=1&pageSize=1000000&getDeletedOnly=false`,
        (res) => {
          const list = res?.data?.filter((p) => p?.id !== currentProjectId);
          setProjects(list);
          setLoading((prev) => ({ ...prev, projects: false }));
        },
        () => setLoading((prev) => ({ ...prev, projects: false }))
      );
    }
  }, [visible]);

  const fetchSampleSizes = () => {
    const projectId = localStorage.getItem("currentProjectID");
    setLoading((prev) => ({ ...prev, sampleSize: true }));

    httpService.get(
      `/api/Datachecks/getAll?projectId=${projectId}`,
      (res) => {
        const data = Array.isArray(res?.data) ? res.data : [];
        const formatted = data.map((item) => ({
          id: item.id,
          name: item.name,
          target: 0,
          isSelected: false,
        }));

        setAvailableSamples(formatted);
        setSelectedSamples(formatted);
        setFilteredSamples([]);
        setLoading((prev) => ({ ...prev, sampleSize: false }));
      },
      (err) => {
        console.error("Error fetching Datachecks:", err);
        setLoading((prev) => ({ ...prev, sampleSize: false }));
      }
    );
  };

  const handleProjectSelect = (projectId) => {
    form.setFieldsValue({ round: projectId });
    fetchSampleSizes();
  };

  const handleSampleFilterChange = (selectedIds) => {
    if (!selectedIds?.length) {
      setFilteredSamples([]);
      return;
    }
    const filtered = selectedSamples.filter((s) => selectedIds.includes(s.id));
    setFilteredSamples(filtered);
  };

  const handleSubmit = () => {
    const selectedProjectId = form.getFieldValue("round");

    // Validate that all selected samples have target filled
    const invalidTarget = filteredSamples.some(
      (s) => s.isSelected && (s.target === null || s.target === undefined)
    );

    if (invalidTarget) {
      setMessage({
        trigger: true,
        type: "danger",
        icon: exclamationMark,
        title: "Missing Target",
        text: "Please enter a target value for all selected sample sizes.",
      });
      setTimeout(() => setMessage({ trigger: false }), 3000);
      return;
    }

    const dataChecks = filteredSamples.map((s) => ({
      dataCheckId: s.id,
      target: s.isSelected ? s.target || 0 : 0, // 0 if Switch off
      isSelected: s.isSelected, // reflect actual Switch state
    }));

    const payload = {
      siteVisitId: siteVisit?.id,
      projectId: selectedProjectId,
      reason: "",
      dataChecks,
    };

    setLoading((prev) => ({ ...prev, submit: true }));
    abortControllerRef.current = new AbortController();

    httpService.post(
      "/api/Site/moveSiteVisitToProject",
      payload,
      (res) => {
        setMessage({
          trigger: true,
          type: "success",
          icon: doneIcon,
          title: "Site visit moved successfully!",
          text: res?.data ?? "Visit moved to the selected round.",
        });
        setTimeout(() => setMessage({ trigger: false }), 3000);
        setLoading((prev) => ({ ...prev, submit: false }));
        form.resetFields();
        setFilteredSamples([]); // reset filtered samples after submit
        onSubmit();
      },
      (error) => {
        console.error(error);
        setMessage({
          trigger: true,
          type: "danger",
          icon: exclamationMark,
          title: "Failed to move site visit",
          text: error?.message ?? "Please try again later.",
        });
        setTimeout(() => setMessage({ trigger: false }), 3000);
        setLoading((prev) => ({ ...prev, submit: false }));
      },
      abortControllerRef.current.signal
    );
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort();
    onClose();
  };

  return (
    <Modal
      title={
        <>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <img
              src={cancelX}
              style={{ marginLeft: "auto", cursor: "pointer" }}
              onClick={handleCancel}
              alt="cancel"
            />
          </div>
          <h2 className="modalHeader">Move visit to another round</h2>
        </>
      }
      visible={visible}
      closable={false}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading.submit}
          onClick={handleSubmit}
        >
          Move to this round
        </Button>,
      ]}
      width={700}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Select the round"
          name="round"
          rules={[{ required: true, message: "Please select a round" }]}
        >
          <Select
            placeholder="Select the round where you'd like to move the visit"
            loading={loading.projects}
            onChange={handleProjectSelect}
            options={projects.map((p) => ({
              value: p.id,
              label: p.projectName,
            }))}
            notFoundContent={
              loading.projects ? (
                <div style={{ textAlign: "center", padding: "8px 0" }}>
                  <Spin size="small" />
                </div>
              ) : (
                "No data"
              )
            }
          />
        </Form.Item>

        {availableSamples.length > 0 && (
          <Form.Item label="Select Sample Sizes to Configure">
            <Select
              mode="multiple"
              placeholder="Select sample sizes"
              onChange={handleSampleFilterChange}
              options={availableSamples.map((s) => ({
                label: s.name,
                value: s.id,
              }))}
            />
          </Form.Item>
        )}

        <div
          className="border p-4"
          style={{
            borderRadius: "8px",
            marginBottom: 20,
            opacity: !form.getFieldValue("round") ? 0.6 : 1,
          }}
        >
          <h3 className="mb-2">Selected Sample Sizes</h3>

          {!form.getFieldValue("round") ? (
            <p style={{ color: "#888" }}>Select a round first.</p>
          ) : loading.sampleSize ? (
            <Spin />
          ) : filteredSamples.length === 0 ? (
            <p style={{ color: "#888" }}>Select sample sizes to configure.</p>
          ) : (
            filteredSamples.map((sample, index) => (
              <Row key={sample.id} gutter={24} style={{ marginTop: 15 }}>
                <Col span={12}>
                  <Form.Item label={sample.name}>
                    <Switch
                      checked={sample.isSelected}
                      onChange={(checked) => {
                        const updated = [...filteredSamples];
                        updated[index].isSelected = checked;
                        if (!checked) updated[index].target = 0;
                        setFilteredSamples(updated);
                      }}
                    />
                  </Form.Item>
                </Col>
                {sample.isSelected && (
                  <Col span={12}>
                    <Form.Item
                      label="Target"
                      required
                      rules={[
                        { required: true, message: "Target is required" },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        value={sample.target}
                        onChange={(val) => {
                          const updated = [...filteredSamples];
                          updated[index].target = val;
                          setFilteredSamples(updated);
                        }}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
            ))
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default MoveSiteVisitModal;
